import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatCard } from "./AdminUI";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

export default function MarketingTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/marketing").then(r => setData(r.data)); }, []);
  if (!data) return <p className="text-sm text-[#222]/45">Loading…</p>;

  return (
    <div data-testid="admin-marketing-tab" className="space-y-6">
      <p className="text-xs text-[#222]/45 bg-black/[0.03] rounded-xl px-3 py-2">
        Every metric below is computed from real, existing data. Metrics with no activity yet show a truthful zero — nothing is fabricated.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Registrations (30d)" value={data.registrations_30d.value} sub={data.registrations_30d.formula} testid="mkt-registrations" />
        <StatCard label="ZIP searches tracked" value={data.zip_searches.total} sub={data.zip_searches.tracking_started_at ? `Since ${new Date(data.zip_searches.tracking_started_at).toLocaleDateString()}` : "Tracking just started"} testid="mkt-zip-searches" />
        <StatCard label="Category clicks tracked" value={data.category_clicks.total} sub={data.category_clicks.formula} testid="mkt-category-clicks" />
        <StatCard label="Search submissions" value={data.search_submits.value} sub={data.search_submits.formula} testid="mkt-search-submits" />
        <StatCard label="Profile views (30d)" value={data.profile_views.last_30d} sub={`${data.profile_views.total} all-time`} testid="mkt-profile-views" />
        <StatCard label="Pros-of-the-Week slots live" value={data.pros_of_the_week.active_slots} sub={`${data.pros_of_the_week.distinct_pros_ever_shown} distinct pros shown ever`} testid="mkt-showcase" />
        <StatCard label="Wall posts" value={data.wall_engagement.posts_on_wall} sub={`${data.wall_engagement.total_reactions} reactions · ${data.wall_engagement.total_comments} comments`} testid="mkt-wall" />
        <StatCard label="Active promotions" value={data.promotions_active.value} sub={data.promotions_active.formula} testid="mkt-promotions" />
        <StatCard label="Featured pros" value={data.featured_pros.value} sub={data.featured_pros.formula} testid="mkt-featured" />
        <StatCard label="Pending category suggestions" value={data.pending_category_suggestions.value} sub="See Category Review tab" testid="mkt-pending-categories" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <ChartBlock title="Top ZIP searches" rows={data.zip_searches.top_zips} color="#2E6B57" />
        <ChartBlock title="Top categories browsed" rows={data.category_clicks.top_categories} color="#3B82F6" />
      </div>

      <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
        <h3 className="text-sm font-bold mb-2">Referral / campaign tracking</h3>
        <p className="text-sm text-[#222]/55">{data.referral_campaigns.note}</p>
      </div>
    </div>
  );
}

function ChartBlock({ title, rows, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-black/5">
      <h3 className="text-sm font-bold mb-3">{title}</h3>
      {(!rows || rows.length === 0) ? <p className="text-sm text-[#222]/40 py-8 text-center">No activity tracked yet</p> : (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={rows} layout="vertical" margin={{ left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10 }} />
            <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
